import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const PrivacyPolicyPage = () => {
  const lastUpdated = "December 2025";
  
  return (
    <div data-testid="privacy-policy-page" className="bg-white">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center">
                <Shield size={28} className="text-teal-600" />
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500 block">
                  Legal
                </span>
                <h1 className="font-heading text-4xl font-bold text-slate-900">
                  Privacy Policy
                </h1>
              </div>
            </motion.div>
            
            <motion.p variants={itemVariants} className="text-slate-500">
              Last Updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto prose prose-slate prose-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                <p className="text-slate-700 m-0">
                  Health Strategy and Leadership ("HSL", "we", "us", or "our") is committed to protecting 
                  your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                  your information when you visit our website or use our services.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900 mt-0">1. Information We Collect</h2>
                <p className="text-slate-600">We may collect information about you in a variety of ways:</p>
                
                <h3 className="font-heading text-xl text-slate-800">Personal Data</h3>
                <p className="text-slate-600">
                  When you contact us, apply for positions, or register for our services, we may collect 
                  personally identifiable information such as:
                </p>
                <ul className="text-slate-600">
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Professional information (organization, job title)</li>
                  <li>Employment history and qualifications (for job applicants)</li>
                  <li>Any other information you choose to provide</li>
                </ul>

                <h3 className="font-heading text-xl text-slate-800">Usage Data</h3>
                <p className="text-slate-600">
                  We may also collect information about how you access and use our website, including:
                </p>
                <ul className="text-slate-600">
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Device and connection information</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">2. How We Use Your Information</h2>
                <p className="text-slate-600">We use the information we collect to:</p>
                <ul className="text-slate-600">
                  <li>Respond to your inquiries and fulfill your requests</li>
                  <li>Process job applications and communicate about employment opportunities</li>
                  <li>Send newsletters and updates (with your consent)</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                  <li>Protect against fraudulent or illegal activity</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">3. Information Sharing</h2>
                <p className="text-slate-600">
                  We do not sell, trade, or rent your personal information to third parties. We may share 
                  your information in the following situations:
                </p>
                <ul className="text-slate-600">
                  <li><strong>Service Providers:</strong> With third-party vendors who assist in operating our website and services</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger or acquisition</li>
                  <li><strong>With Your Consent:</strong> For any other purpose with your explicit agreement</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">4. Data Security</h2>
                <p className="text-slate-600">
                  We implement appropriate technical and organizational security measures to protect your 
                  personal information against unauthorized access, alteration, disclosure, or destruction. 
                  However, no method of transmission over the Internet or electronic storage is 100% secure.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">5. Data Retention</h2>
                <p className="text-slate-600">
                  We retain your personal information only for as long as necessary to fulfill the purposes 
                  for which it was collected, including to satisfy legal, accounting, or reporting requirements. 
                  Job applications are retained for 24 months unless you request earlier deletion.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">6. Your Rights</h2>
                <p className="text-slate-600">Depending on your location, you may have the right to:</p>
                <ul className="text-slate-600">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to or restrict processing of your information</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">7. Cookies</h2>
                <p className="text-slate-600">
                  Our website may use cookies and similar tracking technologies to enhance your browsing 
                  experience. You can control cookies through your browser settings. Disabling cookies may 
                  affect some features of our website.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">8. Third-Party Links</h2>
                <p className="text-slate-600">
                  Our website may contain links to third-party websites. We are not responsible for the 
                  privacy practices or content of these external sites. We encourage you to review the 
                  privacy policies of any third-party sites you visit.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-slate-900">9. Changes to This Policy</h2>
                <p className="text-slate-600">
                  We may update this Privacy Policy from time to time. We will notify you of any changes 
                  by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                  Your continued use of our website after any changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 p-6 rounded-lg">
                <h2 className="font-heading text-2xl text-slate-900 mt-0">10. Contact Us</h2>
                <p className="text-slate-600 mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your rights, 
                  please contact us:
                </p>
                <div className="flex items-center gap-3 text-slate-700">
                  <Mail size={20} className="text-teal-600" />
                  <a href="mailto:info@hsl.com" className="text-teal-600 hover:text-teal-700">
                    info@hsl.com
                  </a>
                </div>
                <p className="text-slate-600 mt-4 mb-0">
                  Health Strategy and Leadership<br />
                  No 4, Natasha Apoti Street, Kado District<br />
                  Abuja, Nigeria
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
